import React, { useState } from 'react';
import { Lock, Eye, EyeOff, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { authService } from '../../lib/supabase';

interface SetPasswordFormProps {
  onSuccess?: () => void;
  onCancel?: () => void;
}

export const SetPasswordForm: React.FC<SetPasswordFormProps> = ({ onSuccess, onCancel }) => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // 密碼強度檢查
  const getPasswordStrength = (pwd: string) => {
    const checks = {
      length: pwd.length >= 8,
      hasUpper: /[A-Z]/.test(pwd),
      hasLower: /[a-z]/.test(pwd),
      hasNumber: /\d/.test(pwd),
      hasSpecial: /[!@#$%^&*(),.?":{}|<>]/.test(pwd)
    };
    
    const score = Object.values(checks).filter(Boolean).length;
    
    return {
      score,
      checks,
      strength: score < 3 ? 'weak' : score < 4 ? 'medium' : 'strong'
    };
  };

  const passwordStrength = getPasswordStrength(password);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // 驗證密碼
    if (password !== confirmPassword) {
      setError('密碼確認不符');
      return;
    }

    if (passwordStrength.score < 3) {
      setError('密碼強度不足，請設定更安全的密碼');
      return;
    }

    setIsLoading(true);

    try {
      const result = await authService.linkPasswordToOAuthAccount(password);
      
      if (result.error) {
        setError(result.error.message || '設定密碼失敗');
      } else {
        setSuccess(true);
        setTimeout(() => {
          onSuccess?.();
        }, 1500);
      }
    } catch (err) {
      setError('設定密碼時發生錯誤，請稍後再試');
    } finally {
      setIsLoading(false);
    }
  };

  if (success) {
    return (
      <div className="w-full max-w-md mx-auto">
        <div className="bg-white rounded-xl shadow-lg shadow-wedding-pink/20 p-8 text-center">
          <div className="flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-wedding-text-title mb-2">密碼設定成功！</h2>
          <p className="text-wedding-text-body">
            現在您可以使用 Google 登入或 Email + 密碼兩種方式登入您的帳戶
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-md mx-auto">
      <div className="bg-white rounded-xl shadow-lg shadow-wedding-pink/20 p-8">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center w-16 h-16 bg-wedding-action/10 rounded-full mx-auto mb-4">
            <Lock className="w-8 h-8 text-wedding-action" />
          </div>
          <h2 className="text-2xl font-bold text-wedding-text-title mb-2">設定登入密碼</h2>
          <p className="text-wedding-text-body">為您的帳戶新增密碼，享受更多登入選擇</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md p-3">
              <div className="flex items-center">
                <AlertCircle className="w-4 h-4 text-red-600 mr-2" />
                <p className="text-red-600 text-sm">{error}</p>
              </div>
            </div>
          )}

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-wedding-text-title mb-2">
              新密碼
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-wedding-action focus:border-wedding-action transition-colors"
                placeholder="請輸入您的新密碼"
                disabled={isLoading}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-wedding-text-body transition-colors"
                disabled={isLoading}
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
            
            {/* 密碼強度指示器 */}
            {password && (
              <div className="mt-2">
                <div className="flex items-center space-x-2 mb-2">
                  <span className="text-sm text-wedding-text-body">密碼強度:</span>
                  <span className={`text-sm font-medium ${
                    passwordStrength.strength === 'weak' ? 'text-red-600' :
                    passwordStrength.strength === 'medium' ? 'text-yellow-600' : 'text-green-600'
                  }`}>
                    {passwordStrength.strength === 'weak' ? '較弱' :
                     passwordStrength.strength === 'medium' ? '中等' : '強'}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div className={`flex items-center ${passwordStrength.checks.length ? 'text-green-600' : 'text-gray-400'}`}>
                    <CheckCircle className="w-3 h-3 mr-1" />
                    8 個字符以上
                  </div>
                  <div className={`flex items-center ${passwordStrength.checks.hasNumber ? 'text-green-600' : 'text-gray-400'}`}>
                    <CheckCircle className="w-3 h-3 mr-1" />
                    包含數字
                  </div>
                  <div className={`flex items-center ${passwordStrength.checks.hasUpper ? 'text-green-600' : 'text-gray-400'}`}>
                    <CheckCircle className="w-3 h-3 mr-1" />
                    包含大寫字母
                  </div>
                  <div className={`flex items-center ${passwordStrength.checks.hasSpecial ? 'text-green-600' : 'text-gray-400'}`}>
                    <CheckCircle className="w-3 h-3 mr-1" />
                    包含特殊字符
                  </div>
                </div>
              </div>
            )}
          </div>

          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-wedding-text-title mb-2">
              確認密碼
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                id="confirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-wedding-action focus:border-wedding-action transition-colors"
                placeholder="請再次輸入密碼"
                disabled={isLoading}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-wedding-text-body transition-colors"
                disabled={isLoading}
              >
                {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          <div className="flex space-x-4">
            {onCancel && (
              <button
                type="button"
                onClick={onCancel}
                className="flex-1 bg-gray-200 text-gray-800 py-3 rounded-md hover:bg-gray-300 transition-colors"
                disabled={isLoading}
              >
                取消
              </button>
            )}
            <button
              type="submit"
              disabled={isLoading || passwordStrength.score < 3 || password !== confirmPassword}
              className="flex-1 bg-wedding-action text-white py-3 rounded-md hover:bg-wedding-action-hover focus:ring-2 focus:ring-wedding-action focus:ring-offset-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  設定中...
                </>
              ) : (
                '設定密碼'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};